import { useMemo, useState } from 'react'
import Seo, { softwareAppSchema } from '../components/Seo'
import Breadcrumbs from '../components/Breadcrumbs'
import Faq from '../components/Faq'
import type { QA } from '../components/Faq'
import ResultSheet from '../components/ResultSheet'
import CalcActions from '../components/CalcActions'
import InfoTip from '../components/InfoTip'
import { Field, SmartField, Segmented } from '../components/Inputs'
import { computeTile, DEFAULT_TILE } from '../calculators/tile'
import type { TileInputs, TileArea } from '../calculators/tile'
import { calcsInCategory } from '../data/calculators'
import { saveSheet, genId } from '../lib/storage'
import { ROOM_PRESETS, CUSTOM_DIMS, CUSTOM_ROOM_KEY, toUnit, matchedPreset, dropdownValue, presetLabel } from '../lib/roomPresets'
import { useLocale, localizedHref } from '../i18n/locale'
import { t } from '../i18n/strings'
import { calcName, categoryName } from '../i18n/calculators'

const PATH = '/calculators/tile/'
const CATEGORY = 'flooring-and-tile'

interface JointOption { label: string; value: number }

interface CalcContent {
  title: string
  desc: string
  h1: string
  intro: string
  jointOptions: JointOption[]
  tileSizeLabel: string
  tileSizeTip: string
  inSuffix: string
  jointWidthLabel: string
  jointWidthTip: string
  backerBoard: string
  wasteTip: string
  wasteNote: string
  primaryName: string
  primaryLabel: (w: number, l: number) => string
  savePrefix: string
  saveSummary: (tiles: number, w: number, l: number, thinset: number, grout: number) => string
  assumptions: (total: number, w: number, l: number, jointLabel: string, wastePct: number) => string
  aboutH2: string
  aboutP1: string
  howH2: string
  howP1: string
  formula: string
  howP2: string
  mistakesH3: string
  mistakes: { pre?: string; strong: string; rest: string }[]
  relatedH2: string
  reviewer: string
  faqs: QA[]
}

const EN: CalcContent = {
  title: 'Tile Calculator — How Much Tile Do I Need? | RenoSheets',
  desc: 'Free tile calculator. Enter your area and tile size to get exact tile count, plus a complete materials list — thinset, grout, spacers and more. No signup.',
  h1: 'Tile Calculator',
  intro: 'Enter your area and tile size for the exact tile count — plus thinset, grout, spacers and everything else you\'ll actually need.',
  jointOptions: [
    { label: '1/16 in — rectified / large-format', value: 0.0625 },
    { label: '1/8 in — narrow / stone', value: 0.125 },
    { label: '3/16 in — standard (default)', value: 0.1875 },
    { label: '1/4 in — wider / rustic look', value: 0.25 },
  ],
  tileSizeLabel: 'Tile size (inches)',
  tileSizeTip: 'Width × length of one tile in inches. Common sizes: 12×12, 12×24, 6×24 plank, 4×4, 3×6 subway. Tile is always sold by inch size in North America.',
  inSuffix: 'in',
  jointWidthLabel: 'Grout joint width',
  jointWidthTip: 'Drives grout quantity. 3/16 in is standard for most ceramic and porcelain. Use 1/8 in for natural stone, 1/16 in for rectified large-format tile.',
  backerBoard: 'Add cement backer board',
  wasteTip: 'Extra tile for cuts, breakage and future repairs. 10% for square rooms; 15–20% for diagonal or patterned layouts.',
  wasteNote: 'Grout is calculated from your tile size and joint width — no guessing. We never pad quantities.',
  primaryName: 'Tile',
  primaryLabel: (w, l) => `of ${w}×${l} in tile`,
  savePrefix: 'Tile',
  saveSummary: (tiles, w, l, thinset, grout) => `${tiles} tiles (${w}×${l} in) · ${thinset} bags thinset · ${grout} bags grout`,
  assumptions: (total, w, l, jointLabel, wastePct) => `Based on ${total} sq ft, ${w}×${l} in tiles, ${jointLabel}, ${wastePct}% waste.`,
  aboutH2: 'What actually affects how much tile you need',
  aboutP1: 'Layout pattern changes tile waste more than almost any other factor. A straight grid laid parallel to the walls is the most efficient use of material — cut-offs from one edge often work on the opposite edge. Diagonal layouts, herringbone, and running-bond patterns all waste more, because the angled or offset cuts rarely reuse cleanly, and you\'re often cutting a fresh tile for every partial piece along a wall. Tile size also interacts with joint width: larger-format tile needs fewer total pieces but a flatter subfloor, since a slight dip that would be invisible under a 12×12 tile becomes an obvious lippage problem under a 24×48 plank tile. Grout quantity scales with joint width and tile size together — smaller tiles with wide joints use noticeably more grout per square foot than large tiles with narrow joints.',
  howH2: 'How to calculate how much tile you need',
  howP1: 'Tile quantity is the total area divided by the size of one tile, with a waste factor for cuts:',
  formula: `tile area (sq ft) = (tile width in × tile length in) ÷ 144
tiles needed = (total area × (1 + waste%)) ÷ tile area
grout (lbs) = 16 × joint width × (tile W + tile L) ÷ (tile W × tile L) × area`,
  howP2: 'Add multiple areas (rooms, hallways, backsplash) and they are summed. Thinset coverage adjusts automatically for tile size: larger tile needs a deeper trowel notch, so coverage per bag drops.',
  mistakesH3: 'Common mistakes',
  mistakes: [
    { pre: 'Not enough ', strong: 'waste', rest: ' for diagonal or patterned layouts — bump to 15–20%.' },
    { pre: 'Buying tile from different ', strong: 'production lots', rest: ' — colour shade varies between batches.' },
    { pre: 'Wrong grout type: ', strong: 'sanded grout is not for tight joints', rest: ' under 1/8 in (it scratches polished surfaces).' },
    { pre: 'Skipping ', strong: 'backer board', rest: ' in wet areas — standard drywall will fail.' },
  ],
  relatedH2: 'Related calculators',
  reviewer: 'Reviewed by the RenoSheets team for calculation accuracy. Method: area ÷ tile size with adjustable waste; thinset from trowel-notch coverage rates; grout from joint-width formula calibrated to manufacturer data. Last updated 2026-05-23. Estimates only — verify quantities before purchasing.',
  faqs: [
    { q: 'How much tile do I need for 100 square feet?', a: 'For 100 sq ft with 12×12 in tiles and 10% waste, you need about 110 tiles. Divide the total area by the area of one tile (tile width × tile length in inches ÷ 144), then add your waste percentage.' },
    { q: 'How much extra tile should I buy for waste?', a: 'Add 10% for straight, square layouts. Use 15–20% for diagonal patterns, patterned tile, or rooms with lots of cuts and angles. Always buy from the same production lot — colours vary between batches.' },
    { q: 'How much thinset do I need?', a: 'One 50 lb bag of thinset covers roughly 40–50 sq ft with a standard 1/4 in square-notch trowel. Large-format tile (over 15 in) needs a bigger notch, so coverage drops to about 30 sq ft per bag.' },
    { q: 'How much grout do I need?', a: 'Grout coverage depends on tile size and joint width. For 12×12 in tile with 3/16 in joints, plan on roughly 0.5 lbs per sq ft — about one 25 lb bag per 50 sq ft. Smaller tiles or wider joints use more grout.' },
    { q: 'What grout joint size should I use?', a: '3/16 in is the most common for standard ceramic floor and wall tile. Use 1/8 in for natural stone and polished porcelain. Large-format rectified tile can go as tight as 1/16 in. Wider joints (1/4 in) suit handmade or rustic tile.' },
    { q: 'Sanded vs unsanded grout — which should I use?', a: 'Use sanded grout for joints 1/8 in or wider. Use unsanded grout for joints narrower than 1/8 in, for glass tile, and for polished or softer stone — sand can scratch delicate surfaces.' },
    { q: 'Do I need cement backer board under tile?', a: 'Yes, on floors and wet areas (showers, tub surrounds). Cement backer board — or an uncoupling membrane — gives tile a stable, moisture-resistant base. Tick the checkbox and the calculator adds the sheets to your list.' },
  ],
}

const FR: CalcContent = {
  title: 'Calculateur de carrelage — Combien de tuiles me faut-il? | RenoSheets',
  desc: 'Calculateur de carrelage gratuit. Entrez votre surface et la taille des tuiles pour obtenir le nombre exact de tuiles, plus une liste complète de matériaux — mortier-colle, coulis, croisillons et plus. Sans inscription.',
  h1: 'Calculateur de carrelage',
  intro: 'Entrez votre surface et la taille des tuiles pour le nombre exact de tuiles — plus le mortier-colle, le coulis, les croisillons et tout le reste dont vous aurez vraiment besoin.',
  jointOptions: [
    { label: '1/16 po — rectifié / grand format', value: 0.0625 },
    { label: '1/8 po — étroit / pierre', value: 0.125 },
    { label: '3/16 po — standard (par défaut)', value: 0.1875 },
    { label: '1/4 po — plus large / aspect rustique', value: 0.25 },
  ],
  tileSizeLabel: 'Taille de la tuile (pouces)',
  tileSizeTip: 'Largeur × longueur d’une tuile en pouces. Tailles courantes : 12×12, 12×24, lame 6×24, 4×4, métro 3×6. Le carrelage se vend toujours en pouces en Amérique du Nord.',
  inSuffix: 'po',
  jointWidthLabel: 'Largeur du joint de coulis',
  jointWidthTip: 'Détermine la quantité de coulis. 3/16 po est la norme pour la plupart des céramiques et porcelaines. Utilisez 1/8 po pour la pierre naturelle, 1/16 po pour le carrelage rectifié grand format.',
  backerBoard: 'Ajouter un panneau d’appui en ciment',
  wasteTip: 'Tuiles en plus pour les coupes, le bris et les réparations futures. 10 % pour les pièces carrées; 15–20 % pour les poses en diagonale ou à motifs.',
  wasteNote: 'Le coulis est calculé à partir de la taille de vos tuiles et de la largeur du joint — sans deviner. Nous ne gonflons jamais les quantités.',
  primaryName: 'Tuiles',
  primaryLabel: (w, l) => `de tuiles de ${w}×${l} po`,
  savePrefix: 'Carrelage',
  saveSummary: (tiles, w, l, thinset, grout) => `${tiles} tuiles (${w}×${l} po) · ${thinset} sacs de mortier-colle · ${grout} sacs de coulis`,
  assumptions: (total, w, l, jointLabel, wastePct) => `Selon ${total} pi², tuiles de ${w}×${l} po, ${jointLabel}, ${wastePct} % de perte.`,
  aboutH2: 'Ce qui influence vraiment la quantité de carrelage nécessaire',
  aboutP1: 'Le motif de pose change la perte de carrelage plus que presque tout autre facteur. Une pose en grille droite, parallèle aux murs, est l’utilisation la plus efficace du matériau — les retailles d’un bord servent souvent au bord opposé. Les poses en diagonale, en chevron ou à joints décalés gaspillent toutes davantage, car les coupes angulaires ou décalées se réutilisent rarement proprement, et il faut souvent couper une tuile neuve pour chaque morceau partiel le long d’un mur. La taille de la tuile interagit aussi avec la largeur du joint : un grand format demande moins de pièces au total mais un sous-plancher plus plat, car un léger creux invisible sous une tuile de 12×12 po devient un problème de désaffleurement évident sous une tuile de 24×48 po. La quantité de coulis dépend à la fois de la largeur du joint et de la taille de la tuile — de petites tuiles à joints larges utilisent nettement plus de coulis au pied carré que de grandes tuiles à joints étroits.',
  howH2: 'Comment calculer la quantité de carrelage nécessaire',
  howP1: 'La quantité de tuiles est la surface totale divisée par la taille d’une tuile, avec un facteur de perte pour les coupes :',
  formula: `surface de tuile (pi²) = (largeur tuile po × longueur tuile po) ÷ 144
tuiles requises = (surface totale × (1 + % perte)) ÷ surface de tuile
coulis (lb) = 16 × largeur du joint × (larg. tuile + long. tuile) ÷ (larg. tuile × long. tuile) × surface`,
  howP2: 'Ajoutez plusieurs surfaces (pièces, corridors, dosseret) et elles s’additionnent. La couverture du mortier-colle s’ajuste automatiquement à la taille des tuiles : une plus grande tuile demande une encoche de truelle plus profonde, donc la couverture par sac diminue.',
  mistakesH3: 'Erreurs fréquentes',
  mistakes: [
    { pre: 'Pas assez de ', strong: 'perte', rest: ' pour les poses en diagonale ou à motifs — montez à 15–20 %.' },
    { pre: 'Acheter des tuiles de différents ', strong: 'lots de production', rest: ' — la teinte de la couleur varie d’un lot à l’autre.' },
    { pre: 'Mauvais type de coulis : ', strong: 'le coulis sablé n’est pas pour les joints serrés', rest: ' de moins de 1/8 po (il égratigne les surfaces polies).' },
    { pre: 'Sauter le ', strong: 'panneau d’appui', rest: ' dans les zones humides — le gypse ordinaire ne tiendra pas.' },
  ],
  relatedH2: 'Calculateurs connexes',
  reviewer: 'Révisé par l’équipe RenoSheets pour l’exactitude des calculs. Méthode : surface ÷ taille de tuile avec marge de perte ajustable; mortier-colle selon les taux de couverture par encoche de truelle; coulis selon une formule de largeur de joint calibrée sur les données du fabricant. Dernière mise à jour 2026-05-23. Estimations seulement — vérifiez les quantités avant d’acheter.',
  faqs: [
    { q: 'Combien de tuiles pour 100 pieds carrés?', a: 'Pour 100 pi² avec des tuiles de 12×12 po et 10 % de perte, il vous faut environ 110 tuiles. Divisez la surface totale par la surface d’une tuile (largeur × longueur en pouces ÷ 144), puis ajoutez votre pourcentage de perte.' },
    { q: 'Combien de tuiles en plus acheter pour la perte?', a: 'Ajoutez 10 % pour les poses droites et carrées. Utilisez 15–20 % pour les motifs en diagonale, le carrelage à motifs, ou les pièces avec beaucoup de coupes et d’angles. Achetez toujours du même lot de production — les couleurs varient d’un lot à l’autre.' },
    { q: 'Combien de mortier-colle me faut-il?', a: 'Un sac de mortier-colle de 50 lb couvre environ 40 à 50 pi² avec une truelle crantée carrée standard de 1/4 po. Le carrelage grand format (plus de 15 po) demande une encoche plus grande, donc la couverture chute à environ 30 pi² par sac.' },
    { q: 'Combien de coulis me faut-il?', a: 'La couverture du coulis dépend de la taille des tuiles et de la largeur du joint. Pour des tuiles de 12×12 po avec des joints de 3/16 po, prévoyez environ 0,5 lb par pi² — soit un sac de 25 lb par 50 pi². Les petites tuiles ou les joints plus larges utilisent plus de coulis.' },
    { q: 'Quelle largeur de joint de coulis utiliser?', a: 'Le 3/16 po est le plus courant pour la céramique et la porcelaine de plancher et de mur standard. Utilisez 1/8 po pour la pierre naturelle et la porcelaine polie. Le carrelage rectifié grand format peut descendre jusqu’à 1/16 po. Les joints plus larges (1/4 po) conviennent aux tuiles faites main ou rustiques.' },
    { q: 'Coulis sablé ou non sablé — lequel utiliser?', a: 'Utilisez du coulis sablé pour les joints de 1/8 po ou plus larges. Utilisez du coulis non sablé pour les joints de moins de 1/8 po, pour le verre, et pour la pierre polie ou plus tendre — le sable peut égratigner les surfaces délicates.' },
    { q: 'Ai-je besoin d’un panneau d’appui en ciment sous le carrelage?', a: 'Oui, sur les planchers et dans les zones humides (douches, pourtours de baignoire). Le panneau d’appui en ciment — ou une membrane de désolidarisation — donne au carrelage une base stable et résistante à l’humidité. Cochez la case et le calculateur ajoute les feuilles à votre liste.' },
  ],
}

export default function TileCalculator() {
  const locale = useLocale()
  const c = locale === 'fr' ? FR : EN
  const L = (en: string) => localizedHref(en, locale)
  const tr = (k: string) => t(locale, k)

  const [i, setI] = useState<TileInputs>(DEFAULT_TILE)
  const [saved, setSaved] = useState(false)
  const r = useMemo(() => computeTile(i, locale), [i, locale])
  const set = (patch: Partial<TileInputs>) => {
    setI((p) => ({ ...p, ...patch }))
    setSaved(false)
  }
  const setArea = (idx: number, patch: Partial<TileArea>) =>
    set({ areas: i.areas.map((a, k) => (k === idx ? { ...a, ...patch } : a)) })
  const cvDim = (ft: number) => toUnit(ft, i.unit)
  const areaDefault = (idx: number) => (locale === 'fr' ? `Surface ${idx + 1}` : `Area ${idx + 1}`)
  const applyPreset = (idx: number, label: string) => {
    if (label === CUSTOM_ROOM_KEY) {
      // Switching to "Custom room" — give the area a default name unless the
      // user already typed a custom one. Empty current (the "Select a room"
      // placeholder state) also gets the default so the input appears.
      const current = i.areas[idx].name
      if (!current || matchedPreset(current)) setArea(idx, { name: areaDefault(idx) })
      return
    }
    const p = ROOM_PRESETS.find((rp) => rp.label === label)
    if (!p) return
    setArea(idx, { name: presetLabel(p, locale), length: cvDim(p.lFt), width: cvDim(p.wFt) })
  }
  // New areas start with an empty name so the dropdown shows the "Select a
  // room" placeholder — user picks a preset or "Custom room" themselves.
  const addArea = () =>
    set({ areas: [...i.areas, { name: '', length: cvDim(CUSTOM_DIMS.lFt), width: cvDim(CUSTOM_DIMS.wFt) }] })
  const removeArea = (idx: number) => set({ areas: i.areas.filter((_, k) => k !== idx) })
  const lin = i.unit === 'imperial' ? (locale === 'fr' ? 'pi' : 'ft') : 'm'

  const onSave = async () => {
    const sqLabel = locale === 'fr' ? 'pi²' : 'sq ft'
    await saveSheet({
      id: genId(),
      type: 'tile',
      name: `${c.savePrefix} — ${r.totalArea} ${sqLabel}`,
      createdAt: new Date().toISOString(),
      summary: c.saveSummary(r.tiles, i.tileWidthIn, i.tileLengthIn, r.thinsetBags, r.groutBags),
      lines: r.lines.filter((l) => l.kind === 'consumable').map((l) => ({ label: l.label, qty: l.qty, unit: l.unit })),
    })
    setSaved(true)
  }

  const related = calcsInCategory(CATEGORY).filter((x) => x.slug !== 'tile')
  const jointLabel = c.jointOptions.find((o) => Math.abs(o.value - i.jointWidthIn) < 0.001)?.label ?? `${i.jointWidthIn} ${c.inSuffix}`

  // Step-by-step "How we got this" content for the ⓘ button on the result card.
  // Mirrors computeTile: sum areas, × (1 + waste%), ÷ tile face area, ceil.
  const sqftWord = locale === 'fr' ? 'pi²' : 'sq ft'
  const tileFaceSqFt = (i.tileWidthIn / 12) * (i.tileLengthIn / 12)
  const buildExplain = (): React.ReactNode => (
    <ol className="list-decimal space-y-1 pl-5">
      <li>
        {i.inputMode === 'area'
          ? (locale === 'fr' ? 'Surface saisie' : 'Area you entered')
          : (locale === 'fr'
              ? `Surface totale (${i.areas.length} ${i.areas.length === 1 ? 'surface' : 'surfaces'})`
              : `Total area (${i.areas.length} ${i.areas.length === 1 ? 'area' : 'areas'})`)}:
        {' '}<strong>{r.totalArea} {sqftWord}</strong>
      </li>
      <li>{locale === 'fr' ? `+ ${i.wastePct} % de perte` : `+ ${i.wastePct}% waste`}: <strong>{r.areaWithWaste} {sqftWord}</strong></li>
      <li>
        {locale === 'fr'
          ? `Surface d'une tuile = ${i.tileWidthIn} × ${i.tileLengthIn} ÷ 144`
          : `Tile face = ${i.tileWidthIn} × ${i.tileLengthIn} ÷ 144`}
        : <strong>{tileFaceSqFt.toFixed(3)} {sqftWord}</strong>
      </li>
      <li>
        {locale === 'fr' ? '÷ surface d’une tuile' : '÷ tile face area'} = <strong>{(r.areaWithWaste / Math.max(0.0001, tileFaceSqFt)).toFixed(1)}</strong>
      </li>
      <li>
        {locale === 'fr' ? 'Arrondi à la tuile entière' : 'Rounded up to whole tiles'} = <strong>{r.tiles} {locale === 'fr' ? (r.tiles === 1 ? 'tuile' : 'tuiles') : (r.tiles === 1 ? 'tile' : 'tiles')}</strong>
      </li>
      <li>
        {locale === 'fr'
          ? `Coulis: 16 × ${i.jointWidthIn} × (${i.tileWidthIn}+${i.tileLengthIn}) ÷ (${i.tileWidthIn}×${i.tileLengthIn}) × ${r.areaWithWaste}`
          : `Grout: 16 × ${i.jointWidthIn} × (${i.tileWidthIn}+${i.tileLengthIn}) ÷ (${i.tileWidthIn}×${i.tileLengthIn}) × ${r.areaWithWaste}`}
        = <strong>{r.groutLbs} {locale === 'fr' ? 'lb' : 'lbs'}</strong>
        {' '}({r.groutBags} {locale === 'fr' ? (r.groutBags === 1 ? 'sac' : 'sacs') : (r.groutBags === 1 ? 'bag' : 'bags')} × 25 lb)
      </li>
    </ol>
  )

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      <Seo title={c.title} description={c.desc} path={PATH} schema={softwareAppSchema(calcName('tile', 'Tile Calculator', locale), c.desc, localizedHref(PATH, locale))} />
      <Breadcrumbs
        items={[
          { name: tr('crumb.home'), href: L('/') },
          { name: categoryName(CATEGORY, 'Flooring & Tile', locale), href: L(`/calculators/${CATEGORY}/`) },
          { name: calcName('tile', 'Tile Calculator', locale), href: L(PATH) },
        ]}
      />

      <h1 className="text-3xl">{c.h1}</h1>
      <p className="mt-1 max-w-content text-text-muted">{c.intro}</p>

      <div className="mt-6 grid gap-6 lg:grid-cols-5">
        <form className="rounded-xl border border-border bg-surface p-5 lg:col-span-3" onSubmit={(e) => e.preventDefault()}>
          <div className="mb-5 flex flex-wrap gap-6">
            <Segmented
              label={tr('calc.input')}
              value={i.inputMode}
              onChange={(v) => {
                if (v === 'area' && i.inputMode === 'dimensions') {
                  const sum = i.areas.reduce((s, a) => s + Math.max(0, a.length) * Math.max(0, a.width), 0)
                  set({ inputMode: v, totalArea: Math.round(sum * 10) / 10 })
                } else {
                  set({ inputMode: v })
                }
              }}
              options={[{ v: 'dimensions', l: tr('calc.dimensions') }, { v: 'area', l: tr('calc.totalArea') }]}
            />
          </div>

          {i.inputMode === 'dimensions' ? (
            <div className="space-y-3">
              {i.areas.map((a, idx) => {
                const isCustom = dropdownValue(a.name) === CUSTOM_ROOM_KEY
                return (
                <div key={idx} className="rounded-md border border-border p-3">
                  <div className="mb-3 flex flex-wrap items-center gap-2">
                    <select
                      value={dropdownValue(a.name)}
                      onChange={(e) => applyPreset(idx, e.target.value)}
                      aria-label={tr('calc.roomType')}
                      className={`rounded-md border border-border bg-surface-2 px-2 py-1.5 text-sm text-text-body focus:border-brand focus:outline-none ${isCustom ? '' : 'w-full sm:w-1/2'}`}
                    >
                      <option value="" disabled>{tr('calc.selectRoom')}</option>
                      {ROOM_PRESETS.map((p) => (
                        <option key={p.label} value={p.label}>{presetLabel(p, locale)}</option>
                      ))}
                      <option value={CUSTOM_ROOM_KEY}>{tr('calc.customRoom')}</option>
                    </select>
                    {isCustom && (
                      <input
                        type="text"
                        value={a.name ?? ''}
                        onChange={(e) => setArea(idx, { name: e.target.value })}
                        placeholder={tr('calc.roomName')}
                        className="min-w-0 flex-1 rounded-md border border-border bg-surface px-2 py-1.5 text-sm text-text outline-none focus:border-brand"
                      />
                    )}
                    {i.areas.length > 1 && (
                      <button type="button" onClick={() => removeArea(idx)} className="text-xs text-danger hover:underline whitespace-nowrap">{tr('calc.remove')}</button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <SmartField label={tr('calc.length')} imperialValue={a.length} onChangeImperial={(n) => setArea(idx, { length: n })} imperialUnit="ft" />
                    <SmartField label={tr('calc.width')} imperialValue={a.width} onChangeImperial={(n) => setArea(idx, { width: n })} imperialUnit="ft" />
                  </div>
                </div>
                )
              })}
              <button type="button" onClick={addArea} className="w-full rounded-md border border-dashed border-border py-2 text-sm font-medium text-brand-text hover:bg-surface-2">
                {tr('calc.addRoom')}
              </button>
            </div>
          ) : (
            <div className="rounded-md border border-border p-3">
              <SmartField
                label={tr('calc.totalArea')}
                imperialValue={i.totalArea}
                onChangeImperial={(n) => set({ totalArea: n })}
                imperialUnit="sq ft"
              />
              <p className="mt-2 text-xs text-text-subtle">{tr('calc.areaHint')}</p>
            </div>
          )}

          {/* Tile size */}
          <div className="mt-4">
            <span className="mb-1 flex items-center text-sm font-medium text-text-body">
              {c.tileSizeLabel}
              <InfoTip text={c.tileSizeTip} />
            </span>
            <div className="grid grid-cols-2 gap-2">
              <SmartField label={tr('calc.width')} imperialValue={i.tileWidthIn} onChangeImperial={(n) => set({ tileWidthIn: n })} imperialUnit="in" />
              <SmartField label={tr('calc.length')} imperialValue={i.tileLengthIn} onChangeImperial={(n) => set({ tileLengthIn: n })} imperialUnit="in" />
            </div>
          </div>

          {/* Grout joint */}
          <div className="mt-4">
            <span className="mb-1 flex items-center text-sm font-medium text-text-body">
              {c.jointWidthLabel}
              <InfoTip text={c.jointWidthTip} />
            </span>
            <select
              value={i.jointWidthIn}
              onChange={(e) => set({ jointWidthIn: parseFloat(e.target.value) })}
              className="w-full rounded-md border border-border bg-surface px-3 py-2.5 text-sm text-text focus:border-brand focus:outline-none"
            >
              {c.jointOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          <label className="mt-4 flex items-center gap-2 text-sm text-text-body">
            <input type="checkbox" checked={i.needBackerBoard} onChange={(e) => set({ needBackerBoard: e.target.checked })} />
            {c.backerBoard}
          </label>

          {/* Adjustable assumptions */}
          <div className="mt-5 rounded-lg bg-surface-2 p-3">
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center font-medium text-text-body">
                {tr('calc.wasteAllowance')}
                <InfoTip text={c.wasteTip} />
              </span>
              <span className="tnum text-brand-text">{i.wastePct}%</span>
            </div>
            <input
              type="range" min={0} max={25} step={1} value={i.wastePct}
              onChange={(e) => set({ wastePct: parseInt(e.target.value) })}
              className="mt-1 w-full accent-brand"
            />
            <p className="mt-2 text-xs text-text-subtle">{c.wasteNote}</p>
          </div>
          <CalcActions onSave={onSave} saved={saved} locale={locale} className="mt-6 border-t border-border pt-4" />
        </form>

        <div className="lg:col-span-2">
          <ResultSheet
            primary={{ name: c.primaryName, label: c.primaryLabel(i.tileWidthIn, i.tileLengthIn), qty: r.tiles, unit: 'tile', query: `${i.tileWidthIn}x${i.tileLengthIn} ceramic floor tile` }}
            secondary={null}
            lines={r.lines}
            assumptions={c.assumptions(r.totalArea, i.tileWidthIn, i.tileLengthIn, jointLabel, i.wastePct)}
            explain={buildExplain()}
          />
        </div>
      </div>

      <article className="mx-auto mt-8 max-w-content text-text-body">
        <h2 className="text-2xl">{c.aboutH2}</h2>
        <p className="mt-3">{c.aboutP1}</p>
        <h2 className="mt-8 text-2xl">{c.howH2}</h2>
        <p className="mt-3">{c.howP1}</p>
        <pre className="mt-3 overflow-x-auto rounded-lg bg-surface-2 p-3 text-sm text-text">{c.formula}</pre>
        <p className="mt-3">{c.howP2}</p>
        <h3 className="mt-6 text-xl">{c.mistakesH3}</h3>
        <ul className="mt-2 list-disc space-y-1 pl-5">
          {c.mistakes.map((m, k) => <li key={k}>{m.pre}<strong>{m.strong}</strong>{m.rest}</li>)}
        </ul>
      </article>

      <Faq items={c.faqs} />

      <section className="mt-10">
        <h2 className="text-2xl">{c.relatedH2}</h2>
        <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
          {related.map((x) => (
            <a
              key={x.slug}
              href={L(`/calculators/${x.slug}/`)}
              className={`rounded-lg border p-3 text-sm transition-colors ${
                x.live
                  ? 'border-border bg-surface-2 font-medium text-brand-text hover:border-brand hover:bg-surface'
                  : 'border-dashed border-border bg-surface-2 text-text-muted'
              }`}
            >
              {calcName(x.slug, x.name, locale)} {x.live ? '' : <span className="text-text-subtle">{tr('calc.soon')}</span>}
            </a>
          ))}
        </div>
      </section>

      <div className="mx-auto mt-8 max-w-content border-t border-border pt-4 text-xs text-text-subtle">{c.reviewer}</div>
    </div>
  )
}
